import { BaseVariantMetadata } from '../../services/productAttributes/types';

// ── Variant-level metadata ──────────────────────────────────────

/**
 * Premier Marine project-specific metadata for each configurator variant.
 *
 * Static fields (populated in the metadata map at build time):
 *   playcanvasPropertyKey, playcanvasMaterialName, sectionId, displayLabel, iconUrl
 *
 * Runtime fields (merged from the Vivid API response):
 *   variantId, enabled, layerOrder
 */
export interface PremierMarineVariantMetadata extends BaseVariantMetadata {
  /**
   * PlayCanvas configuration key — property name for
   * `configurator.setConfig({ [playcanvasPropertyKey]: playcanvasMaterialName })`.
   *
   * Examples: `'railing'`, `'fiberglass'`, `'canvas'`
   */
  playcanvasPropertyKey: string;

  /**
   * Material / colour value sent to PlayCanvas as the property value.
   * May differ from the API variant name (underscore vs space normalisation).
   *
   * API name:     `"Black Hammered"`
   * materialName: `"Black_Hammered"`
   */
  playcanvasMaterialName: string;

  /**
   * Redux settings section ID for grouping in the UI panel.
   * Derived from `PLAYCANVAS_PROP_TO_SECTION_ID` or auto-generated
   * as `playcanvas__${key}`.
   */
  sectionId: string;

  /** Human-readable attribute label shown in the UI (e.g. `"Rail Color"`) */
  displayLabel: string;

  /**
   * Swatch / icon image URL.
   *
   * Resolution order:
   * 1. CMS `Variant.image` (from API) — when uploaded
   * 2. Hardcoded path from the metadata map (fallback)
   * 3. `null` — no image available
   */
  iconUrl: string | null;

  /** CMS variant ID — populated at runtime from the API */
  variantId?: number;

  /** Whether this variant is available for the current product — from API */
  enabled?: boolean;

  /** Rendering / UI sort order — from API `AvailableOption.layerOrder` */
  layerOrder?: number;
}

// ── Mapping constants ───────────────────────────────────────────

/**
 * Maps Vivid API `option.name` → PlayCanvas configuration property key.
 */
export const API_NAME_TO_PLAYCANVAS_KEY: Record<string, string> = {
  'Rail Color':        'railing',
  'Bimini Frame':      'biminiFrame',
  'Upper Panel Color': 'upperPanelColor',
  'Lower Panel Color': 'lowerPanelColor',
  'Base Color':        'furnitureBaseColor',
  'Accent Color':      'furnitureAccentColor',
  'Fiberglass':        'fiberglass',
  'Canvas Color':      'canvas',
  'Flooring Color':    'flooring',
};

/** Reverse mapping: PlayCanvas property key → API `option.name` */
export const PLAYCANVAS_KEY_TO_API_NAME: Record<string, string> =
  Object.fromEntries(
    Object.entries(API_NAME_TO_PLAYCANVAS_KEY).map(([k, v]) => [v, k]),
  );

/**
 * Vivid product IDs for each PlayCanvas boat type.
 */
export const BOAT_TYPE_TO_PRODUCT_ID: Record<string, number> = {
  sunsation: 2646,
  solaris:   2647,
  intrigue:  2648,
  escalante: 2649,
};

/** Reverse mapping: product ID → boat type */
export const PRODUCT_ID_TO_BOAT_TYPE: Record<number, string> = {
  2646: 'sunsation',
  2647: 'solaris',
  2648: 'intrigue',
  2649: 'escalante',
};

/**
 * Maps route param `:id` → Vivid product ID.
 * Derived from ROUTE_TO_BOAT_TYPE (playcanvasConfig) + BOAT_TYPE_TO_PRODUCT_ID.
 *
 * Multiple route slugs can map to the same product — e.g. Sunsation variants.
 */
export const ROUTE_TO_PRODUCT_ID: Record<string, number> = {
  // Sunsation variants
  Sunsation_RL_230_CL: 2646,
  Sunsation_RC_230_DL: 2646,
  Sunspree_RF_230_CL:  2646,

  // Solaris
  Solaris_REV_250_CL:  2647,

  // Intrigue variants
  'intrigue-rl-230-cl': 2648,
  'intrigue-rf-250-cl': 2648,
  Intrigue_Rev_270_CL:  2648,

  // Escalante
  Escalante_310_CL:    2649,
};

/** All unique product IDs — used for preloadProducts(). */
export const ALL_PRODUCT_IDS: number[] =
  Array.from(new Set(Object.values(BOAT_TYPE_TO_PRODUCT_ID)));
// [2646, 2647, 2648, 2649]
